/**
 * journeys-api — typed wrappers for the Booking Journeys data layer, through the
 * @v0y4ge/api-client `apiRaw` choke point (same seam as queues-api / wf-api).
 *
 * These ops are DECLARED and pending in @v0y4ge/api-contracts / api-mocks —
 * Claude Code owns that layer (REQ-063; spec: specs/journeys-seed-package-2026-07-23.md).
 * Until the real ops land, the preview harness serves them from CD preview seeds
 * (`__source: "cd-preview-seed"`), surfaced per screen via <DataSourceStrip>.
 * Ported from the source prototype's localStorage journey store (window.WFM_JOURNEYS).
 */
import { apiRaw } from "@v0y4ge/api-client";
import { reportOpSource } from "../../shell/opSource";
import type { Journey } from "./journey-model";

const tag = <T,>(op: string, p: Promise<T>): Promise<T> =>
  p.then((r) => { reportOpSource(op, r); return r; });
const json = (body: unknown): RequestInit => ({ method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });

export type JourneyStatus = "draft" | "published";
export interface TenantBrand {
  brandColor: string; accentColor: string; backgroundColor: string; textColor: string;
  headingFont: string; bodyFont: string; buttonShape: string;
  /** Public booking domain for this tenant (drives the journeys `baseURL`). */
  domain?: string;
}
/* ── Journey CRUD ──────────────────────────────────────────────────── */
export const listJourneys = (tenantSlug?: string | null) =>
  tag("listJourneys", apiRaw<{ journeys: Journey[] }>(`/v1/admin/journeys${tenantSlug ? `?tenant=${encodeURIComponent(tenantSlug)}` : ""}`, undefined, 'listJourneys'));
export const getJourney = (id: string) =>
  tag("getJourney", apiRaw<Journey>(`/v1/admin/journeys/${id}`, undefined, 'getJourney'));
/** Create seeds server-side from a template (the bank service/location/staff
 *  catalog is embedded by the backend/seed — CD never authors that content). */
export const createJourney = (input: { templateId: string; name: string; type: string; flowOrder?: string | null }) =>
  tag("createJourney", apiRaw<Journey>("/v1/admin/journeys", json(input), 'createJourney'));
/**
 * ⛔ `updateJourney(id, wholeJourney)` IS RETIRED (drop 1, 2026-09-05).
 *
 * It PATCHed the entire journey object at `/v1/admin/journeys/{id}`. Since JAI-1467
 * the admin patch input is strict, so that body is refused for unrecognized keys
 * (`id`, `createdBy`, `updatedAt`, `publishedAt`, `views30d`, `starts30d`,
 * `bookings30d`, `templateId`, `allowBack`, `autoAdvance`, `text`, `_edited`) and for
 * the legacy step names `staff` / `time` / `confirm` under `steps`. The call site
 * swallowed the refusal, so the shipped editor looked like it saved and did not.
 *
 * What replaces it: `patchAdminJourney`, which can only send keys the contract
 * declares, refuses legacy step names BEFORE the request, and never swallows.
 */
const ADMIN_JOURNEY_PATCH_KEYS = [
  "type", "name", "status", "layout", "progress", "header",
  "languages", "order", "steps", "configured", "customText", "footer",
  "advanced", "publishTargets", "event", "staff", "canvas", "screens", "activeScreen",
  "_eventId", "flowOrder", "slug",
] as const;
/** The three step names the contract does NOT have — staff settings are top-level. */
const LEGACY_STEP_NAMES = ["staff", "time", "confirm"];

export const patchAdminJourney = (id: string, patch: Record<string, unknown>) => {
  const unknownKeys = Object.keys(patch).filter((k) => (ADMIN_JOURNEY_PATCH_KEYS as readonly string[]).indexOf(k) < 0);
  if (unknownKeys.length) {
    return Promise.reject(new Error(`Not sent: ${unknownKeys.join(", ")} ${unknownKeys.length === 1 ? "is" : "are"} not part of the journey settings contract.`));
  }
  const steps = patch.steps as Record<string, unknown> | undefined;
  const legacy = steps ? Object.keys(steps).filter((k) => LEGACY_STEP_NAMES.indexOf(k) > -1) : [];
  if (legacy.length) {
    return Promise.reject(new Error(`Not sent: \`steps.${legacy.join("`, `steps.")}\` — the contract has no such pages (staff settings are top-level \`staff\`).`));
  }
  return tag("updateAdminJourney", apiRaw<Journey>(`/v1/admin/journeys/${id}`, { ...json(patch), method: "PATCH" }, 'updateAdminJourney'));
};
export const deleteJourney = (id: string) =>
  tag("deleteJourney", apiRaw<{ ok: boolean }>(`/v1/admin/journeys/${id}`, { method: "DELETE" }, 'deleteJourney'));
export const duplicateJourney = (id: string) =>
  tag("duplicateJourney", apiRaw<Journey>(`/v1/admin/journeys/${id}/duplicate`, json({}), 'duplicateJourney'));
export const setJourneyStatus = (id: string, status: JourneyStatus) =>
  tag("setJourneyStatus", apiRaw<Journey>(`/v1/admin/journeys/${id}/status`, json({ status }), 'setJourneyStatus'));

/* ── Tenant master brand ───────────────────────────────────────────── */
export const getTenantBrand = (tenantSlug: string) =>
  tag("getTenantBrand", apiRaw<TenantBrand>(`/v1/admin/journeys/brand?tenant=${encodeURIComponent(tenantSlug)}`, undefined, 'getTenantBrand'));
